import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';

export interface BackgroundMusic {
  id: string;
  filename: string;
  display_name: string;
  description: string | null;
  category: string | null;
  duration: number | null;
  file_size: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BackgroundMusicOption {
  id: string;
  name: string;
  description: string;
  filename: string;
  category?: string;
}

/**
 * Fetch all active background music from backend API
 */
export const useBackgroundMusic = (activeOnly: boolean = true) => {
  return useQuery<BackgroundMusic[], Error>({
    queryKey: ['background-music', activeOnly],
    queryFn: async () => {
      const params = activeOnly ? '?active_only=true' : '';
      const response = await apiClient.get(`/background-music${params}`);
      return response;
    },
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    cacheTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });
};

/**
 * Get streaming token and URL for background music file
 * This prevents downloads while allowing streaming playback
 */
export const getBackgroundMusicStreamingUrl = async (filename: string): Promise<string> => {
  try {
    const response = await apiClient.get(`/background-music/token/${filename}`);
    return response.streaming_url;
  } catch (error) {
    console.error('Failed to get streaming URL for', filename, error);
    // Fallback to direct URL if token generation fails
    // This ensures backward compatibility during migration
    const baseUrl = import.meta.env.VITE_BACKEND_URL || '/api/v1';
    return `${baseUrl}/background-music/files/${filename}`;
  }
};

/**
 * Get background music URL from backend API (synchronous, for backward compatibility)
 * NOTE: Returns URL with credentials that requires proper CORS setup
 * For new code, consider using getBackgroundMusicStreamingUrl() with tokens instead
 */
export const getBackgroundMusicUrl = (filename: string): string => {
  const baseUrl = import.meta.env.VITE_BACKEND_URL || '/api/v1';
  const url = `${baseUrl}/background-music/files/${filename}`;

  // Note: The caller must set audio.crossOrigin = "use-credentials" to include auth cookies
  // Or use fetch() with credentials: 'include' and create a blob URL
  return url;
};

/**
 * Transform BackgroundMusic[] to BackgroundMusicOption[] for UI components
 */
export const transformToOptions = (musicList: BackgroundMusic[]): BackgroundMusicOption[] => {
  return musicList.map(music => ({
    id: music.filename, // Use filename as ID for backward compatibility
    name: music.display_name,
    description: music.description || '',
    filename: music.filename,
    category: music.category || undefined,
  }));
};

/**
 * Hook that returns background music as formatted options for select components
 */
export const useBackgroundMusicOptions = () => {
  const { data, isLoading, error } = useBackgroundMusic(true);

  return {
    options: data ? transformToOptions(data) : [],
    isLoading,
    error,
    rawData: data,
  };
};
